import type { PlacesByFloor, SelectedPlace } from "@/dummy/places";
import { Place } from "@/types/place";
import { cn } from "@/utils/cn";

interface PlacePickerProps {
  placesByFloor: PlacesByFloor[];
  selectedPlace?: SelectedPlace;
  onChange: (value: SelectedPlace) => void;
  reservedPlaces?: Place[];
}

const PlacePicker = ({
  placesByFloor,
  selectedPlace,
  onChange,
  reservedPlaces,
}: PlacePickerProps) => {
  const getIsDisabled = (placeId: string) =>
    reservedPlaces
      ? reservedPlaces.some(({ plcCd }) => plcCd === placeId)
      : false;

  const handleSelectChange = (plcCd: string) => {
    for (const { floor, places } of placesByFloor) {
      const place = places.find(p => p.plcCd === plcCd);
      if (place) {
        onChange({ plcCd: place.plcCd, plcNm: place.plcNm, floor });
        return;
      }
    }
  };

  return (
    <>
      {/* 모바일: 드롭다운 */}
      <div className="md:hidden">
        <select
          value={selectedPlace?.plcCd || ""}
          onChange={(e) => handleSelectChange(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
        >
          <option value="" disabled>
            장소를 선택하세요
          </option>
          {placesByFloor.map(({ floor, places }) => (
            <optgroup key={floor} label={floor}>
              {places.map(({ plcCd, plcNm }) => (
                <option
                  key={plcCd}
                  value={plcCd}
                  disabled={getIsDisabled(plcCd)}
                >
                  {plcNm}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* 태블릿 이상: 버튼 UI */}
      <div className="hidden h-full overflow-y-auto rounded-lg bg-white md:block">
        {placesByFloor.map(({ floor, places }, index) => (
          <div
            key={floor}
            className={cn(
              "flex items-start gap-3 px-4 py-3",
              index !== placesByFloor.length - 1 &&
                "border-b border-gray-100",
            )}
          >
            <div className="mt-1 shrink-0 rounded-md bg-primary/10 px-2.5 py-1 text-sm font-semibold text-primary">
              {floor}
            </div>
            <div className="flex flex-wrap gap-2">
              {places.map(({ plcCd, plcNm }) => {
                const isSelected = selectedPlace?.plcCd === plcCd;
                const isDisabled = getIsDisabled(plcCd);
                return (
                  <button
                    key={plcCd}
                    type="button"
                    disabled={isDisabled}
                    className={cn(
                      "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all duration-150",
                      isSelected
                        ? "border-primary bg-primary text-white shadow-sm"
                        : "border-gray-200 bg-white text-gray-600 hover:-translate-y-px hover:border-primary/50 hover:text-primary hover:shadow-sm",
                      isDisabled && "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300",
                    )}
                    onClick={() => onChange({ plcCd, plcNm, floor })}
                  >
                    {plcNm}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PlacePicker;
